import NodeCache from 'node-cache'
import getConfig from 'next/config'
import type { GetServerSidePropsContext } from 'next'
import { logger, nullLogger } from '@mc/next-logger'
import { redisClient, isRedisCacheEnabled } from '@mc/next-cache'
import {
  get as getCore,
  getDevAccessHeaders,
  FIXED_HEADERS_FOR_API,
} from '@mc/next-api-core'
import { getForwardedHeaders } from '../../utils/forwardHeaders'

const cacheLogger =
  process.env.CACHE_DEBUG_LOGGING === 'true' ? logger : nullLogger

const { serverRuntimeConfig } = getConfig()

const { NEXT_API_CACHE, NEXT_API_CACHE_TTL } = serverRuntimeConfig ?? {}
const CACHE_TTL = NEXT_API_CACHE_TTL || 3600

const ENV_NAME = process.env.OTEL_SERVICE_NAME || 'unknown_service'

const getHeaders = (ctx: GetServerSidePropsContext) => ({
  ...FIXED_HEADERS_FOR_API,
  ...getDevAccessHeaders(),
  ...getForwardedHeaders(ctx),
})

/**
 * NOTES ON CACHING
 *
 * There are two parallel caching strategies used in this API service. They are:
 *  1. (legacy) In-memory API cache
 *  2. (new) API cache with Redis
 *
 * Legacy in-memory API cache:
 *  - Prior to introducing Redis this was the entirety of our caching strategy.
 *  - All API calls were cached on Node machines in a simple in-memory cache that
 *    would get wiped out every time the pod restarted.
 *  - These caches are also not shared, each pod has its own cache.
 *
 * API cache with Redis:
 *  - Redis is a shared cache that all pods can access.
 *  - Redis is faster than our APIs, but slower than the in-memory cache.
 *  - Therefore we're using Redis as a middle layer between the in-memory cache and the API.
 *  - To maximize in-memory hits since our data fetchers are often run in parallel,
 *    we use promise coalescing to reduce duplicate requests.
 *  - Hierarchy: fastCache -> Redis -> API
 */

// This is the legacy in-memory cache.
const cache = new NodeCache()

// Utility to safely convert a value to a number.
const safeNumber = (n: unknown) => {
  try {
    const val = Number(n)
    return Number.isNaN(val) ? null : val
  } catch (e) {
    return null
  }
}

// This is the in-memory cache we use in tandem with Redis.
// We're keeping it separate for clearer logical separation.
const FAST_CACHE_TTL = safeNumber(process.env.FAST_CACHE_TTL) || 60 // seconds
const fastCache = new NodeCache({ stdTTL: FAST_CACHE_TTL })
const pendingRequests = new NodeCache({
  stdTTL: FAST_CACHE_TTL,
  useClones: false, // pending requests are promises, which we don't want to clone
})

export const get = async <T = unknown>(
  ctx: GetServerSidePropsContext,
  path = '',
): Promise<T> => {
  const headers = getHeaders(ctx)
  return getCore(headers, path)
}

export const getWithCache = async <T>(
  ctx: GetServerSidePropsContext,
  path = '',
  { ttl = CACHE_TTL, keys = [] }: { ttl?: number; keys?: string[] } = {},
): Promise<T> => {
  const cacheKey = [path, ...keys].join('_')

  if (cache.has(cacheKey)) {
    cacheLogger.log('In-memory cache hit', { cacheKey })
    return cache.get(cacheKey) as T
  }

  cacheLogger.log('In-memory cache miss', { cacheKey })

  const t0 = performance.now()
  const response = await get<T>(ctx, path)
  const t1 = performance.now()
  cacheLogger.log('API request time', { path, time: t1 - t0 })

  if (NEXT_API_CACHE && response) {
    cache.set(cacheKey, response, ttl)
    cacheLogger.log('In-memory cache set', { cacheKey, ttl })
  }

  return response
}

/**
 * Utility to add generic data to the nextjs cache.
 * `get/post` only supports HTTP requests
 * This method invokes an arbitrary promise passed in as `getter`
 * then caches/retrieves its result
 */
export const retrieveWithCache = async <T>(
  cacheKey: string,
  getter: () => Promise<T>,
  { ttl = CACHE_TTL }: { ttl?: number } = {},
) => {
  if (cache.has(cacheKey)) {
    return cache.get(cacheKey) as T
  }
  const result = await getter()
  if (NEXT_API_CACHE && result) {
    cache.set(cacheKey, result, ttl)
  }
  return result
}

/**
 * `getWithRedisCache` utilizes Redis as an intermediary cache layer between
 * a short-lived in-memory cache and the API. It uses promise coalescing to
 * avoid race conditions when fetching the same key in parallel.
 */
export const getWithRedisCache = async <T>(
  ctx: GetServerSidePropsContext,
  path = '',
  { ttl = CACHE_TTL, keys = [] }: { ttl?: number; keys?: string[] } = {},
): Promise<T> => {
  // Short circuit if Redis cache is disabled
  const isEnabled = await isRedisCacheEnabled(ctx)
  if (!isEnabled) {
    cacheLogger.log('Redis cache disabled', { path, keys })
    return getWithCache(ctx, path, { ttl, keys })
  }
  if (!redisClient) {
    cacheLogger.error('Redis client not available', { path, keys })
    return getWithCache(ctx, path, { ttl, keys })
  }

  // multi-layer cache
  const NS = `prepw_cache:${ENV_NAME}:api`
  const redisKey = [NS, path, ...keys].join('_')

  if (fastCache.has(redisKey)) {
    cacheLogger.log('Fast cache hit', { redisKey })
    return fastCache.get(redisKey) as T
  } else {
    cacheLogger.log('Fast cache miss', { redisKey })
  }

  // promise coalescing
  if (pendingRequests.has(redisKey)) {
    cacheLogger.log('Promise coalescing hit', { redisKey })
    return pendingRequests.get(redisKey) as Promise<T>
  } else {
    cacheLogger.log('Promise coalescing miss', { redisKey })
  }

  const fetchPromise = (async () => {
    let cached: string | null = null
    try {
      cached = (await redisClient.get(redisKey)) || null
      const cachedSize = getSize(cached || '')
      cacheLogger.log('Fetched Redis key', { redisKey, cachedSize })
    } catch (e) {
      logger.error('Error fetching Redis key', { error: String(e) })
    }

    if (cached) {
      try {
        const parsed = JSON.parse(cached) as T
        cacheLogger.log('Returning cached Redis response', { redisKey })
        fastCache.set(redisKey, parsed)
        cacheLogger.log('Set Fast cache', { redisKey, source: 'Redis' })
        return parsed
      } catch (e) {
        logger.error('Error parsing cached Redis response', e)
      }
    } else {
      cacheLogger.log('Redis cache miss', { redisKey })
    }

    // get from API
    const t0 = performance.now()
    const response = await get<T>(ctx, path)
    const t1 = performance.now()
    cacheLogger.log('API request time', { path, time: t1 - t0 })
    try {
      const data = JSON.stringify(response)
      await redisClient?.set(redisKey, data, 'EX', ttl)
      const cachedSize = getSize(data)
      cacheLogger.log('Set Redis cache', { redisKey, cachedSize, ttl })
    } catch (e) {
      logger.error('Error setting Redis cache', { error: String(e) })
    }

    fastCache.set(redisKey, response)
    cacheLogger.log('Set Fast cache', { redisKey, source: 'API' })
    return response
  })()

  pendingRequests.set(redisKey, fetchPromise)
  return fetchPromise
}

const getSize = (data: string) => {
  const total = Buffer.from(data).byteLength
  return `${(total / 1000).toFixed(0)} kB`
}
